import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

const ProgressPanel = ({ progressSteps, embedded = false, darkMode = true }) => {
  if (!progressSteps || progressSteps.length === 0) return null;

  const completed = progressSteps.filter((s) => s.status === "completed").length;
  const total = progressSteps.length;
  const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
  const done = percentage >= 100;

  return (
    <div
      className={
        embedded
          ? "relative w-full h-auto flex-none block"
          : "fixed z-[9999] transition-all duration-500 ease-in-out"
      }
      style={
        embedded
          ? { width: "100%" }
          : { top: "20px", right: "20px", width: "380px" }
      }
    >
      <div className={`relative rounded-2xl overflow-hidden ${darkMode ? "glass-sheen glass-strong shadow-e2" : "bg-[#F8FBFF] border border-blue-100 shadow-sm"}`}>
        {/* Header */}
        <div className={`px-4 py-3 border-b flex justify-between items-center ${darkMode ? "hairline" : "border-blue-100"}`}>
          <div className="flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              {!done && (
                <span className={`absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping ${darkMode ? "bg-accent-400" : "bg-blue-400"}`} />
              )}
              <span
                className={`relative inline-flex rounded-full h-2.5 w-2.5 ${
                  done ? (darkMode ? "bg-emerald-400" : "bg-emerald-500") : (darkMode ? "bg-accent-400" : "bg-blue-500")
                }`}
              />
            </span>
            <span className={`text-sm font-semibold tracking-wide ${darkMode ? "text-slate-100" : "text-slate-800"}`}>
              {done ? "Analysis Complete" : "Analysis in Progress"}
            </span>
          </div>

          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${darkMode ? "text-[#04121F] bg-accent-gradient shadow-glow-sm" : "bg-blue-100 text-blue-700"}`}>
            {percentage}%
          </span>
        </div>

        {/* Progress bar */}
        <div className={`w-full h-1.5 ${darkMode ? "bg-white/[0.06]" : "bg-blue-50"}`}>
          <motion.div
            className={`h-full relative ${darkMode ? "bg-accent-gradient shadow-glow-sm" : "bg-blue-500"}`}
            initial={{ width: 0 }}
            animate={{ width: `${percentage}%` }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="absolute right-0 top-0 bottom-0 w-1 bg-white/90" />
          </motion.div>
        </div>

        {/* Steps */}
        <div className="max-h-[320px] overflow-y-auto scrollbar-transparent p-2 space-y-1">
          {progressSteps.map((step, index) => (
            <motion.div
              key={`${step.node}-${index}`}
              layout
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-colors duration-200 ${
                step.status === "completed"
                  ? (darkMode ? "bg-emerald-500/[0.08] text-emerald-200" : "bg-emerald-50 text-emerald-700")
                  : step.status === "started"
                  ? (darkMode ? "bg-accent-400/[0.08] text-accent-100 border-l-2 border-accent-400" : "bg-blue-50 text-blue-700 border-l-2 border-blue-500")
                  : (darkMode ? "text-slate-500" : "text-slate-400")
              }`}
            >
              <div className="flex items-center gap-2 truncate">
                {step.status === "completed" ? (
                  <Check className={`w-3.5 h-3.5 shrink-0 ${darkMode ? "text-emerald-400" : "text-emerald-600"}`} strokeWidth={3} />
                ) : step.status === "started" ? (
                  <div className={`w-3.5 h-3.5 border-2 border-t-transparent rounded-full animate-spin shrink-0 ${darkMode ? "border-accent-400" : "border-blue-500"}`} />
                ) : (
                  <div className={`w-3.5 h-3.5 rounded-full border shrink-0 ${darkMode ? "border-slate-600" : "border-slate-300"}`} />
                )}
                <span className="truncate font-medium">{step.node}</span>
              </div>

              {step.count > 0 && (
                <span className={`shrink-0 text-xs font-bold px-1.5 py-0.5 rounded ${darkMode ? "bg-white/[0.06] text-slate-300" : "bg-blue-100 text-blue-700"}`}>
                  {step.count}
                </span>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressPanel;
